import { Children, createContext, isValidElement, useContext, useEffect, useMemo, useState } from "react";

const RouterContext = createContext(null);

const normalizePath = (path = "/") => {
	if (!path || path === "/") {
		return "/";
	}

	const [pathname] = path.split("?");
	const trimmed = pathname.replace(/\/+/g, "/").replace(/^\/+/, "").replace(/\/+$/, "");
	return trimmed || "/";
};

const resolveLocation = (target, base = window.location.href) => {
	const url = new URL(target, base);
	return {
		pathname: normalizePath(url.pathname),
		search: url.search,
		hash: url.hash,
	};
};

const matchRoute = (routeProps, pathname) => {
	if (routeProps.index) {
		return pathname === "/";
	}

	if (routeProps.path === "*") {
		return true;
	}

	const routePath = normalizePath(routeProps.path || "");
	return routePath === pathname;
};

export const Router = ({ children }) => {
	const [location, setLocation] = useState(() => resolveLocation(window.location.href));

	useEffect(() => {
		const handlePopState = () => {
			setLocation(resolveLocation(window.location.href));
		};

		window.addEventListener("popstate", handlePopState);
		return () => window.removeEventListener("popstate", handlePopState);
	}, []);

	const navigate = (to, options = {}) => {
		const nextLocation = resolveLocation(to, window.location.href);
		const nextUrl = `${nextLocation.pathname}${nextLocation.search}${nextLocation.hash}`;
		const method = options.replace ? "replaceState" : "pushState";
		window.history[method](null, "", nextUrl);
		setLocation(nextLocation);
	};

	const value = useMemo(() => ({ location, navigate }), [location]);

	return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
};

export const useLocation = () => {
	const context = useContext(RouterContext);
	return context.location;
};

export const useNavigate = () => {
	const context = useContext(RouterContext);
	return context.navigate;
};

export const Routes = ({ children }) => {
	const { location } = useContext(RouterContext);
	const pathname = normalizePath(location.pathname);

	const route = Children.toArray(children).find((child) => {
		if (!isValidElement(child)) {
			return false;
		}
		return matchRoute(child.props, pathname);
	});

	return route && isValidElement(route) ? route.props.element : null;
};

export const Route = () => null;
